/**
 * A FOLHA DE IMPRESSÃO do romaneio (13/08, pedido do Lucas): uma página só,
 * amigável para papel — cabeçalho da marca, datas, itens, fotos pequenas,
 * mapa da entrega e linhas de assinatura.
 *
 * Compartilhada entre o Escritório (histórico de romaneios) e o Meus pedidos
 * (o dono imprime o romaneio do próprio pedido). A janela imprime sozinha no
 * `window.onload` — que só dispara depois de TODAS as imagens, então fotos e
 * mapa não saem em branco.
 */

import { api } from '../api.js';
import { escapar, dataHora } from './formato.js';
import { avisarBloqueante } from './aviso.js';

export interface RomaneioParaImprimir {
  numero: string;
  criado_em: string;
  retirado_em: string | null;
  entregue_em: string | null;
  motorista_nome: string | null;
  encarregado: string | null;
  recebedor: string | null;
  obs: string | null;
  obraNome: string | null;
  obraEndereco: string | null;
  foto_retirada_url: string | null;
  fotosEntrega: string[];
  lat_entrega: number | null;
  lng_entrega: number | null;
  itens: { codigo: string; nome: string; nome_no_ato?: string | null; quantidade: number; unidade: string | null }[];
}

/**
 * Baixa uma imagem pelo canal autenticado (token, sem cookie) e a devolve
 * EMBUTIDA como data:. A janela de impressão não manda o cookie de sessão
 * (SameSite numa janela about:blank) — foi o mapa 401 SÓ na folha, visto no
 * log em 13/08. Embutida, a imagem não depende de rede nem de sessão.
 */
async function imagemEmbutida(url: string): Promise<string> {
  // `api.foto` põe o prefixo da API (no desenvolvimento ela mora em outra
  // porta — resolver contra a página mandava o pedido ao servidor do Vite,
  // que devolvia o index.html no lugar da imagem).
  const local = await api.fotoAutenticada(api.foto(url) ?? url);
  const blob = await (await fetch(local)).blob();
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => resolve(String(leitor.result));
    leitor.onerror = () => reject(new Error('leitura falhou'));
    leitor.readAsDataURL(blob);
  });
}

export function imprimirRomaneio(r: RomaneioParaImprimir): void {
  // A janela abre JÁ, dentro do gesto do clique — pop-up pedido depois de um
  // `await` é bloqueado pelo navegador. O conteúdo entra quando as imagens
  // terminarem de ser embutidas.
  const win = window.open('', '_blank');
  if (!win) {
    avisarBloqueante('O navegador bloqueou a janela de impressão — libere o pop-up.');
    return;
  }
  win.document.write('<p style="font-family:Arial,sans-serif;color:#555">Montando a folha…</p>');
  void montarFolha(win, r).catch(() => {
    win.document.body.innerHTML =
      '<p style="font-family:Arial,sans-serif;color:#a00">Não consegui montar a folha — feche e tente de novo.</p>';
  });
}

async function montarFolha(win: Window, r: RomaneioParaImprimir): Promise<void> {
  const mapa =
    r.lat_entrega !== null && r.lng_entrega !== null
      ? await imagemEmbutida(`/api/fotos/mapa?lat=${r.lat_entrega}&lng=${r.lng_entrega}&largura=640&altura=220`).catch(() => null)
      : null;
  const fotos = (
    await Promise.all(
      [
        ...(r.foto_retirada_url ? [{ url: r.foto_retirada_url, legenda: 'Retirada' }] : []),
        ...r.fotosEntrega.map((u) => ({ url: u, legenda: 'Entrega' })),
      ].map(async (f) => {
        const dados = await imagemEmbutida(f.url).catch(() => null);
        return dados ? { url: dados, legenda: f.legenda } : null;
      }),
    )
  ).filter((f): f is { url: string; legenda: string } => f !== null);

  // Limpa o "Montando a folha…" e recomeça o documento — sem o open(), o
  // write() só emendaria a folha depois do aviso.
  win.document.open();
  win.document.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
    <title>Romaneio ${escapar(r.numero)}</title>
    <style>
      @page { margin: 12mm; }
      body { font-family: Arial, sans-serif; color: #111; margin: 0; font-size: 0.82rem; }
      h1 { font-size: 1.05rem; color: #164A7F; border-bottom: 3px solid #C9A961;
           padding-bottom: 0.3rem; margin: 0 0 0.15rem; }
      .sub { color: #555; font-size: 0.78rem; margin: 0 0 0.6rem; }
      table { width: 100%; border-collapse: collapse; margin: 0.4rem 0 0.7rem; }
      th, td { border: 1px solid #555; padding: 0.28rem 0.45rem; text-align: left; font-size: 0.78rem; }
      thead th { background: #eee; }
      .dados td { border: none; padding: 0.1rem 0.5rem 0.1rem 0; font-size: 0.8rem; }
      .dados b { color: #164A7F; }
      h2 { font-size: 0.85rem; color: #164A7F; margin: 0.6rem 0 0.2rem; }
      .midia { display: flex; align-items: flex-start; gap: 0.6rem; }
      .fotos { display: flex; flex-wrap: wrap; gap: 0.35rem; }
      .fotos figure { margin: 0; text-align: center; font-size: 0.62rem; color: #555; }
      .fotos img { height: 5.4rem; border: 1px solid #999; }
      .mapa img { height: 10.5rem; border: 1px solid #999; }
      .assina { margin-top: 1.4rem; display: flex; gap: 2.5rem; }
      .assina div { flex: 1; border-top: 1px solid #333; padding-top: 0.25rem;
                    font-size: 0.72rem; color: #444; text-align: center; }
    </style></head><body>
    <h1>Ademaldo Construções — Romaneio ${escapar(r.numero)}</h1>
    <p class="sub">${escapar(r.obraNome ?? 'Concerto de ferramentas')}${
      r.obraEndereco ? ` · ${escapar(r.obraEndereco)}` : ''
    }</p>
    <table class="dados"><tbody>
      <tr><td><b>Criado:</b> ${dataHora(r.criado_em)}</td>
          <td><b>Retirado:</b> ${r.retirado_em ? dataHora(r.retirado_em) : '—'}</td>
          <td><b>Entregue:</b> ${r.entregue_em ? dataHora(r.entregue_em) : '—'}</td></tr>
      <tr><td><b>Motorista:</b> ${escapar(r.motorista_nome ?? '—')}</td>
          <td><b>Encarregado:</b> ${escapar(r.encarregado ?? '—')}</td>
          <td><b>Recebeu:</b> ${escapar(r.recebedor ?? '—')}</td></tr>
    </tbody></table>
    <table><thead><tr><th>Código</th><th>Item</th><th>Qtd.</th><th>Un.</th></tr></thead>
      <tbody>${r.itens
        .map(
          (i) =>
            `<tr><td>${escapar(i.codigo)}</td><td>${escapar(i.nome_no_ato ?? i.nome)}</td><td>${i.quantidade}</td><td>${escapar(i.unidade ?? '')}</td></tr>`,
        )
        .join('')}</tbody></table>
    ${
      fotos.length || mapa
        ? `<div class="midia">
             ${
               fotos.length
                 ? `<div><h2>Fotos</h2><div class="fotos">${fotos
                     .map((f) => `<figure><img src="${escapar(f.url)}"><figcaption>${f.legenda}</figcaption></figure>`)
                     .join('')}</div></div>`
                 : ''
             }
             ${mapa ? `<div><h2>Local da entrega</h2><div class="mapa"><img src="${escapar(mapa)}"></div></div>` : ''}
           </div>`
        : ''
    }
    ${r.obs ? `<p class="sub" style="margin-top:0.5rem">Obs.: ${escapar(r.obs)}</p>` : ''}
    <div class="assina"><div>Conferido por</div><div>Recebido por</div></div>
    <script>window.onload = () => window.print();<\/script>
    </body></html>`);
  win.document.close();
}
